import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import {
	HlrCurrenciesCreateResponse,
	HlrCurrenciesDeleteRequest,
	HlrCurrenciesDeleteResponse,
	HlrCurrenciesReadRequest,
	HlrCurrenciesUpdateResponse,
	HlrSystemCurrenciesResponse
} from '@helpers/types/hlr';
import { AbstractCRUDService, AlarisApiService } from '@campaign-portal/components-library';
import { HlrCurrencies } from '@helpers/types/models';
import { ReadRequest, ReadResponse } from '@campaign-portal/namespace/common/implementations';
import { Filter, RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { HlrExtendableRefBookService } from '@helpers/providers/hlr-extendable-ref-book.service';

@Injectable({
	providedIn: 'root'
})
export class CurrenciesService extends HlrExtendableRefBookService<HlrCurrencies> implements AbstractCRUDService {


	readonly entity = '';
	readonly title = '';
	override readonly loading$ = new BehaviorSubject<boolean>(false);
	readonly create = this.update;

	constructor(
		private readonly api: AlarisApiService
	) {
		super();
	}

	override process(resp: ReadResponse<HlrCurrencies[]>): ReadResponse<HlrCurrencies[]> {
		const data = resp.Data ?? [];
		this.list$.next(data);
		this.map$.next(data.reduce((map, curr) => map.set(curr.id, curr), new Map()));
		return resp;
	}

	read(params?: ReadRequest): Observable<ReadResponse<HlrCurrencies[]>> {
		let parsedParams: HlrCurrenciesReadRequest = {
			active: 'name',
			direction: 'asc',
		}
		if (params) {
			parsedParams = this.prepareParams(params);
		}
		return this.api.loader<ReadResponse<HlrCurrencies[]>>('Currencies.Read', parsedParams, this.loading$)
			.pipe(map(resp => this.process(resp)));
	}

	update(params: HlrCurrencies): Observable<{ Data: HlrCurrencies[], Success: boolean, Total: number }> {
		const method = params.id ? 'Currencies.Update' : 'Currencies.Create';
		return this.api.loader<HlrCurrenciesUpdateResponse
			| HlrCurrenciesCreateResponse>(method, params, this.loading$).pipe(
				map(resp => ({
					Data: resp.data ?? [],
					Success: true,
					Total: resp.data?.length ?? 0
				}))
			);
	}

	delete(params: HlrCurrenciesDeleteRequest): Observable<HlrCurrenciesDeleteResponse> {
		return this.api.loader<HlrCurrenciesDeleteResponse>('Currencies.Delete', params, this.loading$);
	}

	getSystemCurrency(): Observable<HlrCurrencies> {
		return this.api.loader<HlrSystemCurrenciesResponse>('Currencies.GetSysCurrency').pipe(
			map(res => res.sysCurrency)
		)
	}

	readActive(): Observable<ReadResponse<HlrCurrencies[]>> {
		return this.api.loader<any>('Currencies.GetActive', {}, this.loading$).pipe(
			map(resp => ({
				Success: true,
				Total: resp.data?.length ?? 0,
				Data: resp.data ?? []
			})),
			map(resp => this.process(resp)) // обязательно
		);
	}

	private prepareParams(params: RPCRequestParams): HlrCurrenciesReadRequest {
		return {
			offset: params.Paging?.Skip ?? 0,
			limit: params.Paging?.Take ?? 25,
			active: 'name',
			direction: 'asc',
			filters: params.Filters
				? params.Filters.map((filter) => {
					return this.parseFilterField(filter);
				})
				: []
		};
	}

	private parseFilterField(filter: Filter): { key: string, value: string } {
		const variable = filter.Field;
		const value = filter.Value;
		switch (variable) {
			case 'name':
				return { key: 'name', value: value + '' };
			case 'currencySymbol':
				return { key: 'currency_symbol', value: value + '' };
			default:
				return { key: variable, value: value + '' };
		}
	}

}
